import './clinical-study-content.js';

const wrap = (value, count) => (count ? (value + count) % count : 0);

// A non-product comparison section has only a closed dialog to style.
// Keep it closed until its nonblocking stylesheet is ready, including early clicks.
const waitForClinicalStyles = (link, signal) => {
  if (signal.aborted) return Promise.reject(new Error('Clinical Results disconnected'));
  if (!link || link.sheet) return Promise.resolve();
  if (link.dataset.loadError) return Promise.reject(new Error('Clinical Results stylesheet failed to load'));
  return new Promise((resolve, reject) => {
    const cleanup = () => {
      link.removeEventListener('load', loaded);
      link.removeEventListener('error', failed);
      signal.removeEventListener('abort', failed);
    };
    const loaded = () => { cleanup(); resolve(); };
    const failed = () => { cleanup(); reject(new Error('Clinical Results stylesheet unavailable')); };
    link.addEventListener('load', loaded, { once: true });
    link.addEventListener('error', failed, { once: true });
    signal.addEventListener('abort', failed, { once: true });
  });
};

class ClinicalResults extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    const signal = this.abort.signal;
    this.dialog = this.querySelector("dialog");
    this.opener = this.querySelector("[data-open-clinical]");
    if (!this.dialog || !this.opener) return;
    this.opener.addEventListener(
      "click",
      async () => {
        if (this.opening || this.dialog.open) return;
        this.opening = true;
        try {
          const stylesheet = this.dataset.stylesheetId ? document.getElementById(this.dataset.stylesheetId) : null;
          await waitForClinicalStyles(stylesheet, signal);
          if (signal.aborted || !this.isConnected) return;
          if (stylesheet) stylesheet.media = 'all';
          this.dialog.showModal();
          this.previousOverflow = document.documentElement.style.overflow;
          document.documentElement.style.overflow = "hidden";
          this.querySelector("[data-close-clinical]")?.focus();
        } catch (error) {
          if (!signal.aborted) console.error(error);
        } finally {
          this.opening = false;
        }
      },
      { signal },
    );
    this.querySelector("[data-close-clinical]")?.addEventListener(
      "click",
      () => this.dialog.close(),
      { signal },
    );
    this.dialog.addEventListener(
      "click",
      (event) => {
        if (event.target !== this.dialog) return;
        const bounds = this.dialog.getBoundingClientRect();
        if (
          event.clientX < bounds.left ||
          event.clientX > bounds.right ||
          event.clientY < bounds.top ||
          event.clientY > bounds.bottom
        )
          this.dialog.close();
      },
      { signal },
    );
    this.dialog.addEventListener(
      "keydown",
      (event) => {
        if (
          event.key !== "Tab" ||
          event.defaultPrevented ||
          event.altKey ||
          event.ctrlKey ||
          event.metaKey ||
          !this.dialog.open ||
          !this.dialog.contains(document.activeElement)
        )
          return;
        // Include the scroll region even when browser preferences skip it.
        const focusable = Array.from(
          this.dialog.querySelectorAll(
            'a[href], button, input, select, textarea, summary, [tabindex]',
          ),
        ).filter(
          (element) =>
            element.tabIndex >= 0 &&
            !element.matches(":disabled") &&
            !element.closest("[inert]") &&
            element.getClientRects().length > 0 &&
            getComputedStyle(element).visibility === "visible",
        );
        if (!focusable.length) return;
        const index = focusable.indexOf(document.activeElement);
        const next = index < 0 ? (event.shiftKey ? focusable.length - 1 : 0) :
          wrap(index + (event.shiftKey ? -1 : 1), focusable.length);
        event.preventDefault();
        focusable[next].focus();
      },
      { signal },
    );
    this.dialog.addEventListener(
      "close",
      () => {
        document.documentElement.style.overflow = this.previousOverflow ?? "";
        this.opener.focus();
      },
      { signal },
    );
  }
  disconnectedCallback() {
    if (this.dialog?.open) {
      this.dialog.close();
      document.documentElement.style.overflow = this.previousOverflow ?? "";
    }
    this.abort?.abort();
  }
}

if (!customElements.get('clinical-results')) customElements.define('clinical-results', ClinicalResults);
